import type { ReactNode } from 'react';
import { AlertTriangle, Ban, Lock, RotateCcw, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { ROTULO_DE_VERSAO, type Diagnostico, type StatusDeVersao } from '@/features/workflow/tipos';
import { ROTULO_DE_DIAGNOSTICO } from '@/features/workflow/tipos';
import type { Falha } from '@/features/workflow/recusa';

/**
 * As peças que as telas de fluxo compartilham.
 *
 * A mais importante é `AvisoDeFalha`: é nela que a distinção entre recusa e
 * erro vira pixel. Recusa é âmbar e explicativa, erro é vermelho e oferece
 * retentativa — dar a mesma cara às duas ensinaria a quem opera que toda
 * mensagem da tela é "deu problema", quando metade delas é "o processo diz que
 * não, e aqui está o porquê".
 */

// ── falhas ─────────────────────────────────────────────────────────────────

const APARENCIA: Record<
  Falha['tipo'],
  { icone: typeof AlertTriangle; classe: string; titulo: string }
> = {
  recusa: {
    icone: Ban,
    classe: 'border-warning/30 bg-warning/10 text-warning',
    titulo: 'O processo recusou esta ação',
  },
  conflito: {
    icone: RotateCcw,
    classe: 'border-warning/30 bg-warning/10 text-warning',
    titulo: 'O chamado mudou enquanto esta tela estava aberta',
  },
  permissao: {
    icone: Lock,
    classe: 'border-warning/30 bg-warning/10 text-warning',
    titulo: 'Sem permissão para esta ação',
  },
  erro: {
    icone: ShieldAlert,
    classe: 'border-destructive/30 bg-destructive/5 text-destructive',
    titulo: 'Algo deu errado',
  },
};

/**
 * Uma falha, com o tratamento que o tipo dela pede.
 *
 * `aoRecarregar` só é oferecido em conflito e em erro: numa recusa, repetir a
 * mesma requisição devolveria a mesma resposta, e um botão que promete o
 * contrário é pior do que nenhum botão.
 */
export function AvisoDeFalha({
  falha,
  aoRecarregar,
}: {
  falha: Falha;
  aoRecarregar?: () => void;
}) {
  const { icone: Icone, classe, titulo } = APARENCIA[falha.tipo];
  const oferecerRecarga = aoRecarregar && (falha.tipo === 'conflito' || falha.tipo === 'erro');

  return (
    <div
      role="alert"
      className={cn('space-y-2 rounded-md border px-3 py-2.5 text-sm', classe)}
    >
      <div className="flex items-start gap-2">
        <Icone className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold">{titulo}</p>
          <p className="text-foreground/90">{falha.mensagem}</p>
          {/* A promessa só aparece com o botão que a cumpre: dizer "recarregar
              traz o estado de agora" sem oferecer o recarregamento manda a
              pessoa procurar um botão que não existe. */}
          {falha.tipo === 'conflito' && oferecerRecarga ? (
            <p className="text-xs opacity-80">
              Recarregar traz o estado de agora — e, com ele, as ações que ainda valem.
            </p>
          ) : null}
          {falha.requestId ? (
            <p className="font-mono text-[11px] opacity-80">
              Referência para o suporte: {falha.requestId}
            </p>
          ) : null}
        </div>
      </div>

      {oferecerRecarga ? (
        <div className="pl-6">
          <Button
            variant="secondary"
            size="sm"
            onClick={aoRecarregar}
            icon={<RotateCcw className="h-4 w-4" aria-hidden />}
          >
            Recarregar
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Alguém mexeu nisto enquanto você escrevia."
 *
 * O aviso que fecha a corrida **antes** de a gravação acontecer. Um formulário
 * semeado com o que veio do servidor guarda a assinatura do que copiou (ver
 * `@/lib/rascunho`); quando a consulta traz outro conteúdo nos mesmos campos e
 * há edição local por cima, gravar apagaria o trabalho da outra pessoa. Por
 * isso o botão de gravar fica desabilitado enquanto este aviso está na tela.
 *
 * Não descarta nada sozinho: o texto de quem está escrevendo é trabalho, e
 * jogá-lo fora para "atualizar a tela" seria trocar um defeito por outro. Quem
 * descarta é a pessoa, no botão — depois de ler o que chegou.
 */
export function AvisoDeRegistroMudado({ aoDescartar }: { aoDescartar: () => void }) {
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning"
    >
      <AlertTriangle className="h-4 w-4 flex-none" aria-hidden />
      <p className="min-w-0 flex-1 text-foreground/90">
        Outra pessoa alterou estes campos enquanto você editava. O que está digitado aqui continua
        seu, mas gravar está travado: salvar por cima apagaria o que ela escreveu. Recarregue para
        ler o texto de agora — e reescreva o que ainda fizer sentido.
      </p>
      <Button
        variant="secondary"
        size="sm"
        onClick={aoDescartar}
        icon={<RotateCcw className="h-4 w-4" aria-hidden />}
      >
        Descartar e trazer o texto de agora
      </Button>
    </div>
  );
}

// ── etiquetas ──────────────────────────────────────────────────────────────

const CLASSE_DE_VERSAO: Record<StatusDeVersao, string> = {
  DRAFT: 'bg-muted text-muted-foreground ring-border',
  PUBLISHED: 'bg-success/12 text-success ring-success/25',
  RETIRED: 'bg-warning/12 text-warning ring-warning/30',
};

/** Estado da versão em cor **e** em palavra — cor sozinha não comunica. */
export function EtiquetaDeVersao({ status }: { status: StatusDeVersao }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        CLASSE_DE_VERSAO[status],
      )}
    >
      {ROTULO_DE_VERSAO[status]}
    </span>
  );
}

export function Chave({ valor, titulo }: { valor: string; titulo?: string }) {
  return (
    <code
      title={titulo}
      className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
    >
      {valor}
    </code>
  );
}

// ── diagnósticos ───────────────────────────────────────────────────────────

/**
 * **Todos** os problemas, com o caminho de cada um.
 *
 * A API devolve a lista inteira de uma vez, e não o primeiro problema, porque
 * quem escreveu um grafo com seis erros merece ver os seis — corrigir um,
 * publicar, descobrir o segundo e repetir seis vezes é o pior uso possível do
 * tempo de quem administra. Mostrar só o primeiro aqui jogaria fora
 * exatamente o que o servidor se deu ao trabalho de reunir.
 */
export function ListaDeDiagnosticos({ itens }: { itens: readonly Diagnostico[] }) {
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
      {itens.map((problema, indice) => (
        <li
          key={`${problema.path}-${problema.code}-${indice}`}
          className="space-y-1 bg-card px-3 py-2.5"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive ring-1 ring-inset ring-destructive/25">
              {ROTULO_DE_DIAGNOSTICO[problema.code] ?? problema.code}
            </span>
            {/* O caminho é o que permite achar o nó no JSON — fica em fonte
                monoespaçada e selecionável, para copiar e procurar. */}
            <Chave valor={problema.path} titulo="Caminho dentro da definição" />
          </div>
          <p className="text-sm text-foreground">{problema.message}</p>
        </li>
      ))}
    </ul>
  );
}

// ── casca ──────────────────────────────────────────────────────────────────

export function Secao({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: string;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold tracking-tight">{titulo}</h3>
          {descricao ? <p className="max-w-2xl text-sm text-muted-foreground">{descricao}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

export const CLASSE_DE_CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow ' +
  'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground';
